/**
 * Backup-account rollout (ADR 0029): turn unpaired extensions into pairs.
 *
 * The onboarding path for an estate with a working primary PBX and no
 * backup accounts. Every row arrives with a suggested target and can be
 * overridden; ticked rows are created in one pass.
 *
 * This is the only screen in the platform that creates accounts on a live
 * PBX, so it is deliberately slower than it could be: nothing is ticked by
 * default, rows the policy could not decide cannot be ticked at all, and a
 * real run is only reachable after a dry run has said what it would do.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  getBackupSuggestions,
  getFailoverCandidates,
  getServiceStandby,
  runBackupRollout,
} from "@pbxmgr/api";
import type { BackupSuggestion, RolloutItem, RolloutResult } from "@pbxmgr/api";
import { Alert, Badge, Button, Spinner } from "@pbxmgr/ui";
import type { BadgeTone } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";
import { Panel } from "../dashboard/panels";

/** How a suggestion was arrived at, in words an operator can act on. */
const REASON_TEXT: Record<string, string> = {
  designated: "your designation for this domain",
  observed: "where this domain's other pairs went",
  "only-domain": "the only other node and domain",
  undecidable: "no rule and no precedent — choose a target",
};

const REASON_TONE: Record<string, BadgeTone> = {
  designated: "online",
  observed: "online",
  "only-domain": "unknown",
  undecidable: "degraded",
};

function keyOf(s: { node_id: number; domain: string; ext: string }): string {
  return `${s.node_id}|${s.domain}|${s.ext}`;
}

function errorText(err: unknown): string {
  return err instanceof ApiError ? (err.detail ?? err.message) : "request failed";
}

function ResultsTable({
  results,
  dryRun,
}: {
  results: RolloutResult[];
  dryRun: boolean;
}): ReactNode {
  return (
    <table className="ui-table">
      <thead>
        <tr>
          <th>Extension</th>
          <th>{dryRun ? "Would" : "Result"}</th>
          <th>Detail</th>
        </tr>
      </thead>
      <tbody>
        {results.map((r, i) => {
          const state = dryRun ? r.action : r.status;
          const tone: BadgeTone =
            state === "ok" || state === "create"
              ? "online"
              : state === "error"
                ? "offline"
                : "degraded";
          return (
            <tr key={`${r.ext}-${i}`}>
              <td>{r.ext}</td>
              <td>
                <Badge tone={tone}>{state}</Badge>
              </td>
              <td>
                {r.detail ??
                  r.reason ??
                  (r.account_created === false ? "account already existed" : "—")}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Standby clones for hunt groups / attendants (ADR 0037 slice 3). These
 * don't pair to an extension — they are cloned as-is to the standby node so
 * they answer their DID after failover. Read-only for now: the clone write
 * awaits the §3.1 capture of these account types' create + settings shapes.
 */
export function ServiceStandbyPanel(): ReactNode {
  const standby = useQuery({ queryKey: ["service-standby"], queryFn: getServiceStandby });
  const items = standby.data?.items ?? [];
  if (standby.isLoading) return <Spinner />;
  if (items.length === 0) return null; // nothing to show on estates without hunts/attendants
  return (
    <Panel title={`Service-account standby (${standby.data?.gaps ?? 0} without a clone)`}>
      <p>
        Hunt groups and attendants that receive DIDs are made failover-ready by cloning them as-is
        to the standby node. Creating the clone is a manual Vodia step for now; this shows which are
        missing one.
      </p>
      <table className="ui-table">
        <thead>
          <tr>
            <th>Account</th>
            <th>Type</th>
            <th>Standby</th>
            <th>Clone?</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={`${it.node_id}-${it.domain}-${it.account}`}>
              <td>
                {it.account}@{it.domain} (node {it.node_id})
              </td>
              <td>{it.usertype}</td>
              <td>
                {it.standby_domain ? `${it.standby_domain} (node ${it.standby_node_id})` : "—"}
              </td>
              <td>
                <Badge tone={it.has_clone ? "online" : it.actionable ? "offline" : "unknown"}>
                  {it.has_clone ? "cloned" : it.actionable ? "missing" : "no designation"}
                </Badge>
              </td>
              <td>
                {it.instruction ? <span className="ui-empty">{it.instruction}</span> : (it.reason ?? "—")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

export function BackupRolloutPanel(): ReactNode {
  const canManage = usePermission("extensions.manage");
  const qc = useQueryClient();
  const suggestions = useQuery({
    queryKey: ["topo", "backup-suggestions"],
    queryFn: getBackupSuggestions,
  });
  const candidates = useQuery({
    queryKey: ["topo", "failover-candidates"],
    queryFn: getFailoverCandidates,
  });

  // Nothing is pre-ticked: an operator opting in row by row is the point.
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  // Per-row target overrides, keyed the same way as the tick set.
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [results, setResults] = useState<{ dryRun: boolean; items: RolloutResult[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A real run is only offered once a dry run has reported on this selection.
  const [previewed, setPreviewed] = useState(false);

  const items = suggestions.data?.items ?? [];
  const targetOptions = (candidates.data?.items ?? []).flatMap((node) =>
    node.domains.map((domain) => ({
      value: `${node.node_id}|${domain}`,
      label: `${node.node} / ${domain}`,
    })),
  );

  const chosenTarget = (s: BackupSuggestion): string | null => {
    const override = overrides[keyOf(s)];
    if (override) return override;
    return s.target ? `${s.target.node_id}|${s.target.domain}` : null;
  };

  const selected = items.filter((s) => ticked.has(keyOf(s)));
  const selectable = items.filter((s) => chosenTarget(s) !== null);

  const toggle = (s: BackupSuggestion): void => {
    const next = new Set(ticked);
    const k = keyOf(s);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setTicked(next);
    setPreviewed(false);
  };

  const toggleAll = (): void => {
    setTicked(
      selected.length === selectable.length ? new Set() : new Set(selectable.map(keyOf)),
    );
    setPreviewed(false);
  };

  const buildBatch = (): RolloutItem[] =>
    selected.flatMap((s) => {
      const target = chosenTarget(s);
      if (!target) return [];
      const [nodeId, domain] = target.split("|");
      return [
        {
          node_id: s.node_id,
          domain: s.domain,
          ext: s.ext,
          backup_node_id: Number(nodeId),
          backup_domain: domain,
        },
      ];
    });

  const rollout = useMutation({
    mutationFn: ({ dryRun }: { dryRun: boolean }) => runBackupRollout(buildBatch(), dryRun),
    onSuccess: (data) => {
      setError(null);
      setResults({ dryRun: data.dry_run, items: data.items });
      setPreviewed(data.dry_run);
      if (!data.dry_run) {
        setTicked(new Set());
        void qc.invalidateQueries({ queryKey: ["topo"] });
      }
    },
    onError: (err) => setError(errorText(err)),
  });

  if (!canManage) return null;

  return (
    <Panel title={`Create backup accounts (${items.length} unpaired)`}>
      <p>
        Each extension below exists on one node only. Creating its backup makes a real
        account on the target PBX — nothing is selected for you, and a preview always runs
        before anything is created.
      </p>

      {suggestions.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="ui-empty">
          Every discovered extension already has a pair. Refresh the inventory if a node has
          changed.
        </div>
      ) : (
        <>
          <div className="pairing-actions">
            <Button variant="secondary" onClick={toggleAll}>
              {selected.length === selectable.length && selectable.length > 0
                ? "Clear selection"
                : `Select all ${selectable.length} suggested`}
            </Button>
            <Button
              variant="secondary"
              disabled={selected.length === 0 || rollout.isPending}
              onClick={() => rollout.mutate({ dryRun: true })}
            >
              {rollout.isPending ? "Working…" : `Preview ${selected.length}`}
            </Button>
            <Button
              disabled={!previewed || selected.length === 0 || rollout.isPending}
              onClick={() => rollout.mutate({ dryRun: false })}
            >
              Create {selected.length} backup account{selected.length === 1 ? "" : "s"}
            </Button>
          </div>
          {!previewed && selected.length > 0 ? (
            <p className="ui-empty">
              Preview first — it reports what would happen and changes nothing.
            </p>
          ) : null}

          <table className="ui-table">
            <thead>
              <tr>
                <th></th>
                <th>Extension</th>
                <th>Name</th>
                <th>Currently on</th>
                <th>Backup target</th>
                <th>Suggested from</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const k = keyOf(s);
                const target = chosenTarget(s);
                return (
                  <tr key={k}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Create backup for ${s.ext}`}
                        checked={ticked.has(k)}
                        disabled={target === null}
                        onChange={() => toggle(s)}
                      />
                    </td>
                    <td>{s.ext}</td>
                    <td>{s.display_name ?? "—"}</td>
                    <td>{s.domain}</td>
                    <td>
                      <select
                        aria-label={`Backup target for ${s.ext}`}
                        value={target ?? ""}
                        onChange={(e) => {
                          setOverrides({ ...overrides, [k]: e.target.value });
                          setPreviewed(false);
                        }}
                      >
                        <option value="">choose…</option>
                        {targetOptions
                          .filter((o) => !o.value.startsWith(`${s.node_id}|`))
                          .map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td>
                      <Badge tone={REASON_TONE[s.reason] ?? "unknown"}>
                        {REASON_TEXT[s.reason] ?? s.reason}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {error ? <Alert>{error}</Alert> : null}
      {results ? (
        <>
          <h3>{results.dryRun ? "Preview — nothing was created" : "Rollout results"}</h3>
          <ResultsTable results={results.items} dryRun={results.dryRun} />
        </>
      ) : null}
    </Panel>
  );
}
